
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { StarIcon, StarOutlineIcon, SparklesIcon, TrashIcon } from './icons/PlaybackIcons';
import { userService } from '../services/userService';

// A leaner version of the Athlete type for performance data display
export interface AthletePerformance {
  id: string;
  name: string;
  color: string;
  rank: number;
  timeBehind: number; // in milliseconds
  lapsDown: number;
  startTime?: number;
  startCoordinate?: { lat: number; lon: number };
  userId?: string; // User Profile ID if linked
  photoUrl?: string; // Profile photo URL
  hasOfficialResult?: boolean; // Matched with bikedata API
  currentMetrics: {
    speed: number; // in km/h
    power: number | null;
    heartRate: number | null;
  };
  position: { lat: number; lon: number };
}

interface AthleteListProps {
  athletes: AthletePerformance[];
  onHover: (athleteId: string | null) => void;
  onViewProfile: (athleteId: string) => void;
  onSpotlight: (athleteId: string) => void;
  spotlightedAthleteId: string | null;
  onGetInsights: (athleteId: string) => void;
  currentUserId?: string;
  onDelete?: (athleteId: string) => void;
  hasRaceSector?: boolean;
}

const MetricDisplay: React.FC<{ value: string; unit: string; }> = ({ value, unit }) => (
    <div className="flex flex-col items-center">
        <span className="font-semibold text-base text-gray-100">{value}</span>
        <span className="text-xs text-gray-400 uppercase">{unit}</span>
    </div>
);

const formatTimeGap = (ms: number): string => {
  if (ms <= 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `+${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `+${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

const AthleteList: React.FC<AthleteListProps> = ({ 
    athletes, 
    onHover, 
    onViewProfile, 
    onSpotlight, 
    spotlightedAthleteId, 
    onGetInsights,
    currentUserId,
    onDelete,
    hasRaceSector
}) => {
  const [userPhotos, setUserPhotos] = useState<Record<string, string>>({});
  const fetchedUserIds = useRef<Set<string>>(new Set());

  // Convert unique user IDs into a string to safely trigger our effect without reacting to the array re-rendering
  const uniqueUserIdsStr = useMemo(() => {
      return Array.from(new Set(athletes.map(a => a.userId).filter(Boolean))).sort().join(',');
  }, [athletes]);

  useEffect(() => {
      let isMounted = true;
      const fetchPhotos = async () => {
          const userIdsToFetch = uniqueUserIdsStr.split(',').filter(Boolean);
          const newPhotos: Record<string, string> = {};
          let fetchedAny = false;

          await Promise.all(userIdsToFetch.map(async (uid) => {
              if (fetchedUserIds.current.has(uid)) return;
              fetchedUserIds.current.add(uid);
              try {
                  const profile = await userService.getUserProfile(uid);
                  newPhotos[uid] = profile?.photoURL || ''; 
                  fetchedAny = true;
              } catch (e) {
                  newPhotos[uid] = '';
                  fetchedAny = true;
              }
          }));

          if (fetchedAny && isMounted) {
              setUserPhotos(prev => ({ ...prev, ...newPhotos }));
          }
      };
      
      fetchPhotos();
      return () => { isMounted = false; };
  }, [uniqueUserIdsStr]);

  return (
    <div className="flex flex-col flex-grow min-h-0">
      <h3 className="text-lg font-bold text-cyan-400 border-b border-gray-600 pb-2 mb-3 flex-shrink-0">Athletes</h3>
      <ul className="space-y-2 overflow-y-auto pr-2 -mr-2">
        {athletes.map(athlete => {
          const isSpotlighted = athlete.id === spotlightedAthleteId;
          const isMyEntry = currentUserId && (athlete.userId === currentUserId || athlete.id === currentUserId);
          // Fallback image URL using DiceBear
          const safeColor = athlete.color || '#34d399';
          const fallbackImage = `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(athlete.name)}&backgroundColor=${safeColor.substring(1)}`;
          
          let imageUrl = fallbackImage;
          if (athlete.userId && userPhotos[athlete.userId]) {
              imageUrl = userPhotos[athlete.userId];
          } else if (athlete.photoUrl) {
              imageUrl = athlete.photoUrl;
          }

          return (
            <li
              key={athlete.id}
              className={`p-3 rounded-lg transition-colors ${isMyEntry ? 'bg-cyan-900/30 border border-cyan-700/50' : 'bg-gray-700 hover:bg-gray-600'}`}
              onMouseEnter={() => onHover(athlete.id)}
              onMouseLeave={() => onHover(null)}
            >
              <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center justify-center w-12 text-center flex-shrink-0">
                    <span className="text-2xl font-bold text-cyan-400">{athlete.rank}</span>
                    <span className="text-xs font-semibold text-gray-500">POS</span>
                  </div>
                  <div className="flex-shrink-0">
                      <img
                        src={imageUrl}
                        alt={`${athlete.name}'s profile`}
                        className="w-14 h-14 rounded-full object-cover bg-gray-800 border-2"
                        style={{ borderColor: safeColor }}
                        onError={(e) => {
                            if (e.currentTarget.src !== fallbackImage) {
                                e.currentTarget.src = fallbackImage;
                            }
                        }}
                      />
                  </div>
                  <div className="flex-grow flex items-start justify-between min-w-0">
                      <div className="min-w-0 flex-1 mr-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-200 truncate">{athlete.name}</h4>
                            {isMyEntry && <span className="text-[10px] bg-cyan-600 text-white px-1.5 rounded flex-shrink-0">YOU</span>}
                          </div>
                          <div className="flex items-center mt-2 text-center gap-3 justify-start">
                              <MetricDisplay 
                                  value={(athlete.currentMetrics.speed * 0.621371).toFixed(1)} 
                                  unit="mph" 
                              />
                              <MetricDisplay 
                                  value={athlete.currentMetrics.power !== null ? Math.round(athlete.currentMetrics.power).toString() : '—'} 
                                  unit="Watts" 
                              />
                              <MetricDisplay 
                                  value={athlete.currentMetrics.heartRate !== null ? Math.round(athlete.currentMetrics.heartRate).toString() : '—'} 
                                  unit="Bpm" 
                              />
                          </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                          {athlete.rank > 1 ? (
                              <div>
                                  <span className="font-semibold text-xl text-gray-200">
                                      {athlete.lapsDown > 0 
                                          ? `+${athlete.lapsDown} Lap${athlete.lapsDown > 1 ? 's' : ''}` 
                                          : formatTimeGap(athlete.timeBehind)}
                                  </span>
                                  <span className="text-xs text-gray-400 uppercase block">Behind</span>
                              </div>
                          ) : (
                              <div className="text-center">
                                  <span className="font-semibold text-xl text-cyan-400">Leader</span>
                              </div>
                          )}
                      </div>
                  </div>
              </div>
              <div className="w-full mt-3 flex items-center gap-2">
                  <button
                      onClick={() => onSpotlight(athlete.id)}
                      className={`flex-1 flex items-center justify-center px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${isSpotlighted ? 'bg-yellow-500 text-white' : 'text-yellow-300 bg-gray-600/50 hover:bg-yellow-500 hover:text-white'}`}
                  >
                      {isSpotlighted ? <StarIcon className="w-4 h-4 mr-1.5" /> : <StarOutlineIcon className="w-4 h-4 mr-1.5" />}
                      {isSpotlighted ? 'Un-Spotlight' : 'Spotlight'}
                  </button>
                  <button 
                      onClick={() => onViewProfile(athlete.id)}
                      className="flex-1 px-3 py-1.5 text-xs font-semibold text-cyan-200 bg-gray-600/50 rounded-md hover:bg-cyan-500 hover:text-white transition-colors"
                  >
                      View Profile
                  </button>
                  {isMyEntry && (
                    <button
                        onClick={() => onGetInsights(athlete.id)}
                        className={`flex-1 flex items-center justify-center px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                            hasRaceSector
                                ? 'text-amber-300 bg-amber-500/15 border border-amber-500/40 hover:bg-amber-500 hover:text-gray-950 shadow-sm'
                                : 'text-purple-300 bg-gray-600/50 hover:bg-purple-500 hover:text-white'
                        }`}
                        title={hasRaceSector ? "AI Group Ride & Race Sector Insights" : "AI Performance Insights"}
                    >
                        {hasRaceSector ? (
                            <>
                                <span className="mr-1 text-sm leading-none">⚡</span>
                                <span>Sector Insights</span>
                            </>
                        ) : (
                            <>
                                <SparklesIcon className="w-4 h-4 mr-1.5" />
                                <span>Insights</span>
                            </>
                        )}
                    </button>
                  )}
                  {isMyEntry && onDelete && (
                      <button
                        onClick={() => onDelete(athlete.id)}
                        className="px-3 py-1.5 text-xs font-semibold text-red-300 bg-red-900/30 rounded-md hover:bg-red-600 hover:text-white transition-colors"
                        title="Delete your entry"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                  )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  );
};

export default AthleteList;
